

import { createApiLogger } from '../utils/logger';

export interface ApiResponse<T> {
    data: T;
    success?: boolean;
    error?: string;
}

export class ApiService<T> {
    private resourceName: string;
    private token: string;
    private parentResource?: string;
    private parentId?: string;
    private fullResourcePath: string;

    constructor(token: string, resourceName: string, parentResource?: string, parentId?: string) {
        this.resourceName = resourceName;
        this.parentResource = parentResource;
        this.parentId = parentId;
        this.token = token;
        this.fullResourcePath = parentResource && parentId 
            ? `${parentResource}/${parentId}/${resourceName}`
            : resourceName;
    }

    private getHeaders() {
        return {
            'Authorization': `Bearer ${this.token}`,
        };
    }



    private async extractErrorFromResponse(response: Response): Promise<string> {
        try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                if (errorData.error) return errorData.error;
                if (errorData.message) return errorData.message;
                if (typeof errorData === 'string') return errorData;
            }
            const text = await response.text();
            if (text) return text;
        } catch {
            // If we can't parse the error, fall back to status text
        }
        return response.statusText || `HTTP ${response.status}`;
    }

    async getAll(): Promise<T[]> {
        try {
            // For v6, we need to use the REST API directly since we're using custom endpoints
            const response = await fetch(`/api/${this.fullResourcePath}`, {
                method: 'GET',
                headers: this.getHeaders(),
            });
            
            if (!response.ok) {
                const errorMessage = await this.extractErrorFromResponse(response);
                throw new Error(`Failed to fetch ${this.resourceName}: ${errorMessage} (${response.status})`);
            }
            
            let data;
            try {
                data = await response.json();
            } catch (parseError) {
                const logger = createApiLogger('getAll', undefined);
                logger.error(`Failed to parse JSON response for ${this.resourceName}`, { error: parseError instanceof Error ? parseError.message : String(parseError) });
                throw new Error(`Invalid JSON response from server for ${this.resourceName}`);
            }
            
            // Handle different response formats
            if (data && data[this.resourceName]) {
                return Array.isArray(data[this.resourceName]) ? data[this.resourceName] : [];
            }
            if (data && data.data) {
                return Array.isArray(data.data) ? data.data : [];
            }
            // If response is directly an array, return it
            if (Array.isArray(data)) {
                return data;
            }
            // If data is null or undefined, return empty array
            if (data === null || data === undefined) {
                return [];
            }
            // If we get an object but not in expected format, log warning and return empty array
            const logger = createApiLogger('getAll', undefined);
            logger.warn(`Unexpected response format for ${this.resourceName}`, { data });
            return [];
        } catch (error) {
            const logger = createApiLogger('getAll', undefined);
            logger.error(`Error fetching ${this.resourceName}`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async getById(id: string, requireAuth: boolean = true): Promise<T | null> {
        try {
            const headers = requireAuth ? this.getHeaders() : {};
            const response = await fetch(`/api/${this.resourceName}/${id}`, {
                method: 'GET',
                headers: headers,
            });
            
            if (!response.ok) {
                const errorMessage = await this.extractErrorFromResponse(response);
                throw new Error(`Failed to fetch ${this.resourceName} ${id}: ${errorMessage} (${response.status})`);
            }
            
            try {
                return await response.json();
            } catch (parseError) {
                const logger = createApiLogger('getById', undefined);
                logger.error(`Failed to parse JSON response for ${this.resourceName} ${id}`, { error: parseError instanceof Error ? parseError.message : String(parseError) });
                throw new Error(`Invalid JSON response from server for ${this.resourceName} ${id}`);
            }
        } catch (error) {
            const logger = createApiLogger('getById', undefined);
            logger.error(`Error fetching ${this.resourceName} by id`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async getByIdPublic(id: string): Promise<T | null> {
        try {
            // Use the public endpoint - no authentication headers
            const response = await fetch(`/api/public/${this.resourceName}/${id}`, {
                method: 'GET',
            });
            
            if (!response.ok) {
                const errorMessage = await this.extractErrorFromResponse(response);
                throw new Error(`Failed to fetch public ${this.resourceName} ${id}: ${errorMessage} (${response.status})`);
            }
            
            try {
                return await response.json();
            } catch (parseError) {
                const logger = createApiLogger('getByIdPublic', undefined);
                logger.error(`Failed to parse JSON response for public ${this.resourceName} ${id}`, { error: parseError instanceof Error ? parseError.message : String(parseError) });
                throw new Error(`Invalid JSON response from server for public ${this.resourceName} ${id}`);
            }
        } catch (error) {
            const logger = createApiLogger('getByIdPublic', undefined);
            logger.error(`Error fetching public ${this.resourceName} by id`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async getAllPublic(): Promise<T[]> {
        try {
            // For nested resources, use the public endpoint with parent path
            const endpoint = this.parentResource && this.parentId 
                ? `/api/public/${this.parentResource}/${this.parentId}/${this.resourceName}`
                : `/api/public/${this.resourceName}`;
            
            const response = await fetch(endpoint, {
                method: 'GET',
            });
            
            if (!response.ok) {
                const errorMessage = await this.extractErrorFromResponse(response);
                throw new Error(`Failed to fetch public ${this.resourceName}: ${errorMessage} (${response.status})`);
            }
            
            try {
                const data = await response.json();
                // Handle different response formats
                if (Array.isArray(data)) {
                    return data;
                }
                if (data && data[this.resourceName]) {
                    return Array.isArray(data[this.resourceName]) ? data[this.resourceName] : [];
                }
                if (data && data.data) {
                    return Array.isArray(data.data) ? data.data : [];
                }
                return [];
            } catch (parseError) {
                const logger = createApiLogger('getAllPublic', undefined);
                logger.error(`Failed to parse JSON response for public ${this.resourceName}`, { error: parseError instanceof Error ? parseError.message : String(parseError) });
                throw new Error(`Invalid JSON response from server for public ${this.resourceName}`);
            }
        } catch (error) {
            const logger = createApiLogger('getAllPublic', undefined);
            logger.error(`Error fetching public ${this.resourceName}`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async create(id: string, data: T): Promise<ApiResponse<T>> {
        try {
            // For nested resources, use the full resource path
            // For top-level resources, use the specific resource path
            const endpoint = this.parentResource && this.parentId 
                ? `/api/${this.fullResourcePath}`
                : `/api/${this.resourceName}/${id}`;
            
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    ...this.getHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            
            if (!response.ok) {
                const errorMessage = await this.extractErrorFromResponse(response);
                throw new Error(`Failed to create ${this.resourceName}: ${errorMessage} (${response.status})`);
            }
            
            try {
                const result = await response.json();
                return { data: result, success: true };
            } catch (parseError) {
                const logger = createApiLogger('create', undefined);
                logger.error(`Failed to parse JSON response for ${this.resourceName}`, { error: parseError instanceof Error ? parseError.message : String(parseError) });
                throw new Error(`Invalid JSON response from server for ${this.resourceName}`);
            }
        } catch (error) {
            const logger = createApiLogger('create', undefined);
            logger.error(`Error creating ${this.resourceName}`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async update(id: string, data: T): Promise<ApiResponse<T>> {
        try {
            const response = await fetch(`/api/${this.resourceName}/${id}`, {
                method: 'PUT',
                headers: {
                    ...this.getHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            
            if (!response.ok) {
                const errorMessage = await this.extractErrorFromResponse(response);
                throw new Error(`Failed to update ${this.resourceName} ${id}: ${errorMessage} (${response.status})`);
            }
            
            try {
                const result = await response.json();
                return { data: result, success: true };
            } catch (parseError) {
                const logger = createApiLogger('update', undefined);
                logger.error(`Failed to parse JSON response for ${this.resourceName} ${id}`, { error: parseError instanceof Error ? parseError.message : String(parseError) });
                throw new Error(`Invalid JSON response from server for ${this.resourceName} ${id}`);
            }
        } catch (error) {
            const logger = createApiLogger('update', undefined);
            logger.error(`Error updating ${this.resourceName}`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    async delete(id: string): Promise<ApiResponse<void>> {
        try {
            // For nested resources, use the full path with the id in the body
            const endpoint = this.parentResource && this.parentId 
                ? `/api/${this.fullResourcePath}`
                : `/api/${this.resourceName}/${id}`;
            
            const requestOptions: RequestInit = {
                method: 'DELETE',
                headers: this.getHeaders(),
            };
            
            // For nested resources, add the id in the body
            if (this.parentResource && this.parentId) {
                // Convert plural resource name to singular for ID field
                let singularName = this.resourceName;
                if (this.resourceName.endsWith('es')) {
                    singularName = this.resourceName.slice(0, -2); // Remove 'es'
                } else if (this.resourceName.endsWith('s')) {
                    singularName = this.resourceName.slice(0, -1); // Remove 's'
                }
                requestOptions.body = JSON.stringify({ 
                    [singularName + 'Id']: id // e.g., coachId for coaches
                });
                requestOptions.headers = {
                    ...this.getHeaders(),
                    'Content-Type': 'application/json',
                };
            }
            
            const response = await fetch(endpoint, requestOptions);
            
            if (!response.ok) {
                const errorMessage = await this.extractErrorFromResponse(response);
                throw new Error(`Failed to delete ${this.resourceName} ${id}: ${errorMessage} (${response.status})`);
            }
            
            return { data: undefined, success: true };
        } catch (error) {
            const logger = createApiLogger('delete', undefined);
            logger.error(`Error deleting ${this.resourceName}`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }
}

// Factory function to create API services
export const createApiService = <T>(token: string, resourceName: string, parentResource?: string, parentId?: string): ApiService<T> => {
    return new ApiService<T>(token, resourceName, parentResource, parentId);
}; 